import React, {useMemo} from 'react';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {combineDateTime, getDaysDifference, getDateFormat} from '../../../../library/dates';
import {useSelector} from 'react-redux';
import {DEFAULT_TIME_ZONE} from '../../../../globalVariables/locals';
import * as css from './Timeline.css';
import {get} from 'lodash';
import {TState} from '../../../../redux/store.type';
import TimelinePoint from './TimelinePoint';

export interface TimelinePointConfig {
    dateFieldPath: string; // e.g., 'salesValidity.startDate'
    timeFieldPath: string; // e.g., 'salesValidity.startTime'
    label: string; // Translation key or text
    subLabel?: string; // Optional translation key or text
    isActive?: boolean; // Optional: override active state
    showDuration?: boolean; // Whether to show duration to the next point
}

export interface TimelineProps {
    points: TimelinePointConfig[];
    translationNamespace?: string; // Translation namespace (default: 'subscriptions')
    dateFormat?: string; // Date format (default: 'DD.MM.YY')
}

function Timeline({points, translationNamespace = 'subscriptions', dateFormat = 'DD.MM.YY'}: TimelineProps) {
    const {values} = useFormikContext<any>();
    const {t} = useTranslation(translationNamespace);
    const timeZone = useSelector((s: TState) => s.event?.timeZone) || DEFAULT_TIME_ZONE;

    const timelinePoints = useMemo(() => {
        return points.map((pointConfig) => {
            const dateValue = get(values, pointConfig.dateFieldPath);
            const timeValue = get(values, pointConfig.timeFieldPath);
            const combinedDate = dateValue && timeValue? combineDateTime(dateValue, timeValue, timeZone): null;
            const formattedDate = combinedDate ? getDateFormat(combinedDate, dateFormat, timeZone): null;
            const isActive = pointConfig.isActive !== undefined ? pointConfig.isActive: !!combinedDate;

            return {
                date: combinedDate,
                formatted: formattedDate,
                label: t(pointConfig.label),
                subLabel: pointConfig.subLabel ? t(pointConfig.subLabel) : undefined,
                isActive,
                showDuration: pointConfig.showDuration || false
            };
        });
    }, [values, timeZone, t, points, dateFormat]);

    const durations = useMemo(() => {
        const durationArray: (number | null)[] = [];
        
        for(let i = 0; i < timelinePoints.length - 1; i++) {
            const currentPoint = timelinePoints[i];
            const nextPoint = timelinePoints[i + 1];
            
            // Only calculate duration if current point has showDuration enabled
            if(currentPoint.showDuration && currentPoint.date && nextPoint.date) {
                durationArray.push(getDaysDifference(currentPoint.date, nextPoint.date));
            } else {
                durationArray.push(null);
            }
        }

        return durationArray;
    }, [timelinePoints]);

    return (
        <css.container>
            <css.pointsContainer>
                {timelinePoints.map((point, index) => (
                    <TimelinePoint
                        key={index}
                        point={point}
                        duration={durations[index] ?? null}
                        index={index}
                        totalPoints={timelinePoints.length}
                        translationNamespace={translationNamespace}
                    />
                ))}
            </css.pointsContainer>
        </css.container>
    );
}

export default Timeline;

